import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { AuthenticationService } from 'core/authentication/authentication.service';
import { DialogService } from 'core/dialog/dialog.service';
import {
  SelectedAccountManager,
  SelectedAccountManagerService
} from 'core/selected-account-manager/selected-account-manager.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Icon } from '../icon/icon.enum';
import { AppLocalization } from '../entities/app-localization';
import { ImportItemsQuoteService } from '../import-quotes-and-orders/import-items-quote.service';
import { NavBarItem } from './navbar-item.class';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  standalone: false
})

export class NavbarComponent implements OnInit, OnDestroy {
  navItems: NavBarItem[];
  userName: string;
  mobileUserName: string;
  appStrings: AppLocalization;
  appIcon: string;
  selectedMenu: string;
  isOnline: boolean;
  failedLocalServer: boolean;
  importQuoteActive = false;
  subs = new Subscription();

  importActivePages = { quotes: '/quotes', orders: '/orders' };

  roots = {
    dashboard: '/dashboard',
    accounts: '/accounts',
    quotes: '/quotes',
    touchpoints: '/touchpoints',
    tools: '/tools',
    categories: '/catalogs',
  };

  private readonly accountsSubRoutes = ['/accounts-leads', '/leads'];
  private readonly quotesSubRoutes = ['/orders', '/ln-orders', '/invoices'];

  urls = {
    B2B: 'https://b2b.snapon.com',
    RRC: 'https://rrc.snapon.com/',
    SEP: 'https://sep.snapon.com/',
    trueFit: 'https://truefit.snapon.com/',
    industrialCatalog: 'http://snaponindustrialcatalog.com',
    infoResources: 'https://www.snapon.com/indresources',
    accountForm: 'http://www.snaponnewcustomer.com',
    productCatalog: 'https://epc.snapon.com/',
    industrialFlyer: 'http://www.snaponindustrialflyer.com',
    tableau: 'https://tableau.snapon.com/'
  };

  constructor(
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private dialogService: DialogService,
    private deviceService: DeviceDetectorService,
    private importItemsQuoteService: ImportItemsQuoteService,
    private selectedAccountManagerService: SelectedAccountManagerService
  ) {
    this.appInfoService.userDataChanged$.subscribe(result => this.userName = result.UserInfo.Name);
  }

  ngOnInit() {
    this.appStrings = this.appInfoService.appStrings;
    this.isOnline = this.appStateService.state.isOnline;
    this.failedLocalServer = this.appStateService.state.localServerFailure;
    this.userName = this.appInfoService.userData.UserInfo.Name;
    this.mobileUserName = this.getMobileUserName(this.appInfoService.userData.UserInfo.Name);
    this.appIcon = Icon.logo;
    this.selectedMenu = '';

    this.subs.add(this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.handleNavigationEnd(event);
      }
    }));

    this.subs.add(this.appInfoService.countrySelected$.subscribe(() => this.setNavItems()));
    this.subs.add(this.selectedAccountManagerService.selected$.subscribe(newSelectedAM => this.updateDashboardLabel(newSelectedAM)));
    this.subs.add(this.authenticationService.offlineAuthenticationDone$.subscribe(() => this.setNavItemsForOffline()));

    if (!this.appInfoService.userHasAccess() || !this.appInfoService.userCountrySelected()) {
      this.navItems = [];
      return;
    }

    this.subs.add(this.appStateService.offlineStateToggle$.subscribe(onlineState => {
      this.isOnline = onlineState;
      if (!this.isOnline) {
        // set offline-only items on navbar
        this.setNavItemsForOffline();
      }
    }));

    this.setNavItems();

    // Subscribe to destination quote changes
    this.subs.add(this.importItemsQuoteService.getDestinationQuoteId$()
      .subscribe(quoteId => {
        this.updateImportQuoteActive(quoteId);
      }));
  }

  /**
   * Updates importQuoteActive based on destination quote and current route
   */
  private updateImportQuoteActive(quoteId: string | null) {
    const url = this.router.url;
    const isOnImportPage = url === this.importActivePages.quotes ||
      url.startsWith(`${this.importActivePages.quotes}/`) ||
      url === this.importActivePages.orders ||
      url.startsWith(`${this.importActivePages.orders}/`);
    this.importQuoteActive = !!quoteId && isOnImportPage;
  }

  /**
   * Handle navigation end
   * @param event navigation event
   */
  handleNavigationEnd(event) {
    let url = event.url;

    // Update import active state FIRST before checking if menu should be hidden
    const quoteId = this.importItemsQuoteService.getDestinationQuoteId();
    this.updateImportQuoteActive(quoteId);

    if (url.indexOf('/', 1) !== -1) {
      url = url.substring(0, url.indexOf('/', 1));
    }

    if (this.accountsSubRoutes.includes(url)) {
      this.selectedMenu = this.roots.accounts;
    } else if (this.quotesSubRoutes.includes(url)) {
      this.selectedMenu = this.roots.quotes;
    } else {
      this.selectedMenu = url;
    }

    // if home, and from Electron, select the first menu item
    if (event.url.indexOf('/index.html') !== -1 && this.appStateService.state.isElectron && this.navItems.length) {
      this.selectedMenu = this.navItems[0].rootpath;
    }

    // if home, select the first menu item
    if (event.url === '/' && this.navItems && this.navItems.length) {
      this.selectedMenu = this.navItems[0].rootpath;
    }
  }

  /**
   * Setting nav items
   */
  setNavItems() {
    if (this.isOnline) {
      if (this.deviceService.isMobile()) {
        this.setNavItemsForMobile();
      } else if (this.appInfoService.isNSLocaleSelected()) {
        this.setNavItemsForNSLocale();
      } else {
        this.setNavItemsForOnline();
      }
    } else {
      this.setNavItemsForOffline();
    }
  }

  /**
   * Updates the Dashboard nav label with the selected Account Manager name
   * @param selectedAM selected Account Manager, or null when cleared
   */
  private updateDashboardLabel(selectedAM: SelectedAccountManager | null) {
    const dashboardItem = this.navItems?.find(item => item.rootpath === this.roots.dashboard);
    if (!dashboardItem) {
      return;
    }

    dashboardItem.label = selectedAM
      ? `${this.appInfoService.appStrings.menu_Dashboard} - ${selectedAM.name}`
      : this.appInfoService.appStrings.menu_Dashboard;
  }

  private setNavItemsForOnline() {
    this.navItems = [
      {
        label: this.appInfoService.appStrings.menu_Accounts,
        path: this.roots.accounts,
        rootpath: this.roots.accounts
      },
      {
        label: this.appInfoService.appStrings.menu_Quotes,
        path: this.roots.quotes,
        rootpath: this.roots.quotes
      },
      {
        label: this.appInfoService.appStrings.menu_Products,
        path: `${this.roots.tools}${this.roots.categories}`,
        rootpath: this.roots.tools
      }
    ];

    if (this.appInfoService.isAdminTypeUser()) {
      if (this.isOnline) {
        this.navItems.splice(2, 0, {
          label: this.appInfoService.appStrings.menu_Touchpoints,
          path: this.roots.touchpoints,
          rootpath: this.roots.touchpoints
        });
      }

      // Add Dashboard (first)
      this.navItems.unshift({
        label: this.appInfoService.appStrings.menu_Dashboard,
        path: this.roots.dashboard,
        rootpath: this.roots.dashboard
      });

      this.updateDashboardLabel(this.selectedAccountManagerService.selected);
    } else {
      // Add Dashboard (first) and Touchpoints after Accounts for AM
      this.navItems.unshift({
        label: this.appInfoService.appStrings.menu_Dashboard,
        path: '/',
        rootpath: '/'
      });

      if (this.isOnline) {
        this.navItems.splice(2, 0, {
          label: this.appInfoService.appStrings.menu_Touchpoints,
          path: this.roots.touchpoints,
          rootpath: this.roots.touchpoints
        });
      }
    }

  }

  private setNavItemsForMobile() {
    if (this.appInfoService.isNSLocaleSelected()) {
      this.setNavItemsForNSLocale();
      return;
    }

    this.navItems = [
      {
        label: this.appInfoService.appStrings.menu_Accounts,
        path: this.roots.accounts,
        rootpath: this.roots.accounts
      },
      {
        label: this.appInfoService.appStrings.menu_Quotes,
        path: this.roots.quotes,
        rootpath: this.roots.quotes
      },
      {
        label: this.appInfoService.appStrings.menu_Products,
        path: `${this.roots.tools}${this.roots.categories}`,
        rootpath: this.roots.tools
      }];

    if (this.appInfoService.isAdminTypeUser()) {
      if (this.isOnline) {
        this.navItems.splice(2, 0, {
          label: this.appInfoService.appStrings.menu_Touchpoints,
          path: this.roots.touchpoints,
          rootpath: this.roots.touchpoints
        });
      }

      this.navItems.unshift({
        label: this.appInfoService.appStrings.menu_Dashboard,
        path: this.roots.dashboard,
        rootpath: this.roots.dashboard
      });
    } else {
      this.navItems.unshift({
        label: this.appInfoService.appStrings.menu_Dashboard,
        path: '/',
        rootpath: '/'
      });

      if (this.isOnline) {
        this.navItems.splice(2, 0, {
          label: this.appInfoService.appStrings.menu_Touchpoints,
          path: this.roots.touchpoints,
          rootpath: this.roots.touchpoints
        });
      }
    }
  }

  private setNavItemsForOffline() {
    this.navItems = [
      {
        label: this.appInfoService.appStrings.menu_Dashboard,
        path: '/',
        rootpath: '/'
      },
      {
        label: this.appInfoService.appStrings.menu_Accounts,
        path: this.roots.accounts,
        rootpath: this.roots.accounts
      },
      {
        label: this.appInfoService.appStrings.menu_Quotes,
        path: this.roots.quotes,
        rootpath: this.roots.quotes
      }
    ];
    this.selectedMenu = this.navItems[0].rootpath;
  }

  private setNavItemsForNSLocale() {
    this.navItems = [
      {
        label: this.appInfoService.appStrings.menu_Quotes,
        path: this.roots.quotes,
        rootpath: this.roots.quotes
      }
    ];
    this.selectedMenu = this.navItems[0].rootpath;
  }


  private getMobileUserName(userName: string) {
    const names = userName.split(' ').filter(str => str.length > 1);
    return names.slice(0, 2).map(word => word.charAt(0)).join('');
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  /**
   * Logs out
   */
  logout() {
    if (this.isOnline) {
      this.authenticationService.logout();
    } else {
      this.authenticationService.logoutOffline();
      this.router.navigate(['/offline-login']);
    }
  }

  /**
   * Opens IT support info popup
   */
  openHelpInfo() {
    const phoneNumber = '1-800-762-7638';
    const ITemail = 'crmhelp@snapon.com';

    const dialogData = {
      title: this.appStrings.itSupport_title,
      content: `${this.appStrings.contacts_phone}: ${phoneNumber} <br/>
      ${this.appStrings.contacts_email}: ${ITemail}`,
      buttons: [
        {
          label: this.appStrings.ok,
          style: 'primary'
        }
      ]
    };

    this.dialogService.openDialog(dialogData);
  }

}
